import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, MenuItem,
  Select, Typography
} from "@mui/material";
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart,
  Legend, Line, ResponsiveContainer,
  Tooltip, XAxis, YAxis
} from "recharts";
import {
  getDashboardCampaigns        as getCampaigns,
  getDashboardOptouts          as getOptouts,
  getDashboardOptoutsByDpd     as getOptoutsByDpd,
  getDashboardOptoutsHeatmap   as getOptoutsHeatmap,
  getDashboardOptoutsSummary   as getOptoutsSummary,
} from "../../services/pbi/pbiservices";

/*
 ─────────────────────────────────────────────────────────────────────
   BACKEND CONTRACT — see optouts-routes.js for SQL.
   Channel comes in as a prop ("texts" | "emails") from the global
   toggle in PowerBIManagerView; backend filters to that channel.
 ─────────────────────────────────────────────────────────────────────
*/

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const PINK   = "#D4537E";
const GREEN  = "#1D9E75";
const MINT   = "#3DBE92";   // newest bucket — lighter than GREEN
const AMBER  = "#BA7517";
const GRAY   = "#888780";

const GRAN_OPTIONS  = ["Day", "Week", "Month"];
const RANGE_OPTIONS = [
  "7D", "30D", "90D", "1Y",
  "C Wk", "CW+Last",
  "This Mo", "Cur+Last", "Prev 2Mo", "Prev 3Mo",
];

const DPD_BUCKETS = ["1-9","10-19","20-29","30-39","40-49","50-59","60-89","90+"];
const DPD_COLORS  = {
  "1-9":   MINT,
  "10-19": GREEN,
  "20-29": BLUE,
  "30-39": "#185FA5",
  "40-49": AMBER,
  "50-59": "#D4870F",
  "60-89": PINK,
  "90+":   "#A01F50",
};

// Business-hours heatmap window: 8am–10pm
const HEATMAP_HOURS       = ["8a","9a","10a","11a","12p","1p","2p","3p","4p","5p","6p","7p","8p","9p","10p"];
const HEATMAP_HOUR_VALUES = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22];
const HEATMAP_DAYS        = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

// ── Date range helper ─────────────────────────────────────────────
function getDateRange(preset) {
  const now = new Date();
  const y   = now.getFullYear();
  const m   = now.getMonth();
  let start, end;

  const dow        = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);

  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);

  switch (preset) {
    case "7D":      start = new Date(now); start.setDate(now.getDate() - 7);   end = now; break;
    case "30D":     start = new Date(now); start.setDate(now.getDate() - 30);  end = now; break;
    case "90D":     start = new Date(now); start.setDate(now.getDate() - 90);  end = now; break;
    case "1Y":      start = new Date(now); start.setDate(now.getDate() - 365); end = now; break;
    case "C Wk":    start = thisMonday; end = now; break;
    case "CW+Last": start = lastMonday; end = now; break;
    case "This Mo": start = new Date(y, m, 1);     end = now; break;
    case "Cur+Last":start = new Date(y, m - 1, 1); end = now; break;
    case "Prev 2Mo":start = new Date(y, m - 2, 1); end = new Date(y, m, 0); break;
    case "Prev 3Mo":
    default:        start = new Date(y, m - 3, 1); end = new Date(y, m, 0); break;
  }

  const toLocal = (d) => {
    const yy = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mo}-${dy}`;
  };
  return { startDate: toLocal(start), endDate: toLocal(end) };
}

// Previous period = same duration as current, ending the day before current starts.
function getPrevDateRange(startDate, endDate) {
  const parse = (s) => {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  };
  const start  = parse(startDate);
  const end    = parse(endDate);
  const durMs  = end.getTime() - start.getTime();
  const dayMs  = 86_400_000;
  const prevEnd   = new Date(start.getTime() - dayMs);
  const prevStart = new Date(prevEnd.getTime() - durMs);
  const toLocal = (d) => {
    const yy = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mo}-${dy}`;
  };
  return { startDate: toLocal(prevStart), endDate: toLocal(prevEnd) };
}

// ── Helpers ────────────────────────────────────────────────────────
function safeNum(v) { const n = Number(v); return isNaN(n) ? 0 : n; }

function fmt(n, dec = 0) {
  if (n == null || isNaN(n)) return "—";
  return Number(n).toFixed(dec);
}

function fmtDate(val) {
  if (!val) return "";
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

// Build a delta object from current/prev numeric values.
//   up:   true if current value increased vs prev  → drives ▲/▼ arrow
//   good: true if the change is favorable          → drives green/red color
//   text: "+5.2% vs 234" — pct change + actual prev value formatted
//
// For opt-out metrics, lower is better → goodWhenUp defaults to false here.
// `fmtVal` formats the prev value for display (default: comma-formatted int).
function buildDelta(cur, prev, { goodWhenUp = false, fmtVal } = {}) {
  const c = Number(cur);
  const p = Number(prev);
  if (!isFinite(c) || !isFinite(p) || p === 0) return null;
  const pct      = ((c - p) / p) * 100;
  const up       = pct > 0;
  const prevText = fmtVal ? fmtVal(p) : p.toLocaleString();
  return {
    up,
    good: goodWhenUp ? up : !up,
    text: `${up ? "+" : ""}${pct.toFixed(1)}% vs ${prevText}`,
  };
}

// Compute period length in days from a {startDate, endDate} pair.
function daysInRange(startDate, endDate) {
  if (!startDate || !endDate) return 1;
  const ms = new Date(endDate) - new Date(startDate);
  return Math.max(1, Math.round(ms / 86_400_000));
}

// Shift (UTC day-of-week, UTC hour) → (local day-of-week, local hour).
function utcToLocal(dayOfWeek, hourOfDay) {
  const offsetMins = new Date().getTimezoneOffset();
  let totalMins = hourOfDay * 60 - offsetMins;
  let dayShift  = 0;
  while (totalMins < 0)     { totalMins += 1440; dayShift -= 1; }
  while (totalMins >= 1440) { totalMins -= 1440; dayShift += 1; }
  const newHour = Math.floor(totalMins / 60);
  const newDay  = ((dayOfWeek - 1 + dayShift) % 7 + 7) % 7 + 1;
  return { day: newDay, hour: newHour };
}

function heatColor(val, max) {
  if (!val || max === 0) return "#F1EFE8";
  const ratio = val / max;
  if (ratio < 0.2)  return "#FBE6EE";
  if (ratio < 0.4)  return "#F4B5CC";
  if (ratio < 0.65) return "#D4537E";
  return "#7C2549";
}

// ── Sub-components ─────────────────────────────────────────────────
// Delta rendering rules:
//   color = delta.good (true → green, false → red)
//   arrow = ▲ if delta.up, ▼ otherwise — unless delta.arrow === false
// The `good ?? !up` fallback keeps backwards compat with older call sites
// that only set `up` (treating up = bad, the original opt-out semantics).
function KpiCard({ label, value, color = BLUE, delta }) {
  return (
    <Box sx={{ background: "#f7f7f7", borderRadius: 2, p: "10px 14px", minWidth: 0 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {delta && (
        <Typography sx={{
          fontSize: 11,
          color: (delta.good ?? !delta.up) ? "#3B6D11" : "#A32D2D",
          mt: 0.5,
        }}>
          {delta.arrow !== false && (delta.up ? "▲ " : "▼ ")}{delta.text}
        </Typography>
      )}
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, children, height = 200 }) {
  return (
    <Box sx={{
      border: "0.5px solid", borderColor: "divider",
      borderRadius: 2, p: "12px 14px",
    }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

function PillGroup({ label, options, value, onChange, groups }) {
  let groupBoundaries = new Set();
  if (groups) {
    let idx = 0;
    groups.forEach((size, gi) => {
      if (gi < groups.length - 1) { idx += size; groupBoundaries.add(idx); }
    });
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{
        fontSize: 11, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
      }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
              {groupBoundaries.has(i) && (
                <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />
              )}
              <Box
                onClick={() => onChange(opt)}
                sx={{
                  px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                  cursor: "pointer", userSelect: "none",
                  border: "0.5px solid",
                  borderColor: active ? BLUE : "divider",
                  background:  active ? BLUE : "transparent",
                  color:       active ? "#fff" : "text.secondary",
                  fontWeight:  active ? 500 : 400,
                  transition:  "all .15s",
                  "&:hover": { borderColor: BLUE },
                }}
              >
                {opt}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Day × Hour heatmap ─────────────────────────────────────────────
function Heatmap({ data }) {
  const grid = useMemo(() => {
    const map = {};
    (data || []).forEach(r => {
      const dow = Number(r.day_of_week);
      const h   = Number(r.hour_of_day);
      if (!dow || isNaN(h)) return;
      const { day, hour } = utcToLocal(dow, h);
      const key = `${day}_${hour}`;
      map[key] = (map[key] || 0) + safeNum(r.optouts);
    });
    return map;
  }, [data]);

  const max = Math.max(1, ...Object.values(grid));

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: `36px repeat(7, 1fr)`, gap: "2px", fontSize: 10 }}>
        <Box />
        {HEATMAP_DAYS.map(d => (
          <Box key={d} sx={{ textAlign: "center", color: "text.secondary", pb: 0.5 }}>{d}</Box>
        ))}
        {HEATMAP_HOURS.map((h, hi) => (
          <React.Fragment key={h}>
            <Box sx={{ textAlign: "right", pr: 1, color: "text.secondary", lineHeight: "20px", height: 20 }}>
              {h}
            </Box>
            {HEATMAP_DAYS.map((_, di) => {
              const hour = HEATMAP_HOUR_VALUES[hi];
              const val  = grid[`${di + 1}_${hour}`] || 0;
              return (
                <Box key={di} sx={{
                  height: 20, borderRadius: "2px", background: heatColor(val, max),
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 9, color: val > max * 0.6 ? "#fff" : "text.secondary",
                }}>
                  {val || ""}
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mt: 1, fontSize: 10, color: "text.secondary" }}>
        <span>Low</span>
        {["#FBE6EE","#F4B5CC","#D4537E","#7C2549"].map(c => (
          <Box key={c} sx={{ width: 14, height: 10, background: c, borderRadius: "2px" }} />
        ))}
        <span>High</span>
      </Box>
    </Box>
  );
}

// ── DPD bucket table (single channel) ──────────────────────────────
const DPD_GRID = `100px 1fr 1fr 1.5fr`;

function OptoutByDpdTable({ data }) {
  const rows = useMemo(() => {
    const map = {};
    (data || []).forEach(r => {
      const k = r.dpd_bucket;
      if (!map[k]) map[k] = { dpd_bucket: k, optouts_total: 0 };
      map[k].optouts_total += safeNum(r.optouts_total);
    });
    return DPD_BUCKETS.map(b => map[b] || { dpd_bucket: b, optouts_total: 0 });
  }, [data]);

  const total      = rows.reduce((a, r) => a + safeNum(r.optouts_total), 0);
  const grandTotal = total || 1;
  const maxBucket  = Math.max(1, ...rows.map(r => safeNum(r.optouts_total)));

  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, mb: 1.5 }}>
      <Box sx={{
        display: "grid", gridTemplateColumns: DPD_GRID, px: 2, py: 1,
        background: "#f7f7f7", borderBottom: "0.5px solid", borderColor: "divider",
        fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em",
        color: "text.secondary",
      }}>
        <Box>DPD</Box>
        <Box sx={{ textAlign: "right" }}>Opt-outs</Box>
        <Box sx={{ textAlign: "right" }}>% of all</Box>
        <Box sx={{ pl: 2 }}>Distribution</Box>
      </Box>
      {rows.map((row, i) => {
        const v      = safeNum(row.optouts_total);
        const pct    = grandTotal > 0 ? (v / grandTotal) * 100 : 0;
        const barPct = maxBucket > 0 ? (v / maxBucket) * 100 : 0;
        const color  = DPD_COLORS[row.dpd_bucket] || GRAY;
        return (
          <Box key={row.dpd_bucket} sx={{
            display: "grid", gridTemplateColumns: DPD_GRID, px: 2, py: "7px",
            fontSize: 12, borderBottom: "0.5px solid", borderColor: "divider",
            background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
            "&:hover": { background: "rgba(212,83,126,0.06)" },
            alignItems: "center",
          }}>
            <Box>
              <Box component="span" sx={{
                display: "inline-block", px: "7px", py: "1px", borderRadius: "20px",
                fontSize: 11, fontWeight: 500,
                bgcolor: `${color}18`, color, border: `0.5px solid ${color}44`,
              }}>{row.dpd_bucket}</Box>
            </Box>
            <Box sx={{ textAlign: "right", color: v > 0 ? PINK : "text.disabled", fontWeight: 500 }}>
              {v > 0 ? v.toLocaleString() : "—"}
            </Box>
            <Box sx={{ textAlign: "right", color: "text.secondary" }}>
              {v > 0 ? `${pct.toFixed(1)}%` : "—"}
            </Box>
            <Box sx={{ pl: 2, pr: 1 }}>
              <Box sx={{ background: "#f0f0f0", height: 6, borderRadius: 1 }}>
                <Box sx={{
                  background: color, height: "100%",
                  width: `${barPct}%`, borderRadius: 1, transition: "width .3s",
                }} />
              </Box>
            </Box>
          </Box>
        );
      })}
      <Box sx={{
        display: "grid", gridTemplateColumns: DPD_GRID, px: 2, py: "8px",
        fontSize: 12, background: "#fdf0f5", borderTop: `1px solid ${PINK}33`,
      }}>
        <Box sx={{ fontSize: 11, fontWeight: 700, color: PINK }}>Total</Box>
        <Box sx={{ textAlign: "right", color: PINK, fontWeight: 700 }}>{total.toLocaleString()}</Box>
        <Box sx={{ textAlign: "right", color: "text.secondary", fontWeight: 700 }}>100%</Box>
        <Box />
      </Box>
    </Box>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────
export default function OptOutDashboard({ channel }) {
  const [gran,        setGran]        = useState("Day");
  const [rangePreset, setRangePreset] = useState("30D");
  const [campaign,    setCampaign]    = useState("All");
  const [campaigns,   setCampaigns]   = useState(["All"]);
  const [dpdBucket,   setDpdBucket]   = useState("All");

  const [optouts,     setOptouts]     = useState([]);
  const [byDpd,       setByDpd]       = useState([]);
  const [heatmap,     setHeatmap]     = useState([]);
  const [summary,     setSummary]     = useState({});
  const [prevSummary, setPrevSummary] = useState({}); // for rate / avgDay deltas
  const [loading,     setLoading]     = useState(true);
  const [hmLoading,   setHmLoading]   = useState(false);
  const [error,       setError]       = useState("");

  const channelParam = (channel || "texts").toLowerCase();
  const ch           = channelParam === "emails" ? "Emails" : "Texts";

  useEffect(() => {
    getCampaigns()
      .then(data => setCampaigns(["All", ...(data || []).map(c => c.name)]))
      .catch(() => {});
  }, []);

  const params = useMemo(() => ({
    ...getDateRange(rangePreset),
    campaign:    campaign === "All" ? undefined : campaign,
    channel:     channelParam,
    granularity: gran.toLowerCase(),
  }), [rangePreset, campaign, channelParam, gran]);

  // Same shape as `params` but with date range shifted to the prior period.
  // The summary endpoint returns prev_total_optouts already, but it doesn't
  // give us prev_total_contacted — so we fetch the full prev summary in
  // parallel for accurate rate / avgDay deltas.
  const prevParams = useMemo(() => ({
    ...getPrevDateRange(params.startDate, params.endDate),
    campaign:    campaign === "All" ? undefined : campaign,
    channel:     channelParam,
    granularity: gran.toLowerCase(),
  }), [params.startDate, params.endDate, campaign, channelParam, gran]);

  // Main loader (everything except heatmap, which depends on dpdBucket)
  const loadAll = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [o, d, s, ps] = await Promise.all([
        getOptouts(params),
        getOptoutsByDpd(params),
        getOptoutsSummary(params),
        // Prev period: failures non-fatal — KPIs just won't show deltas.
        getOptoutsSummary(prevParams).catch(() => ({})),
      ]);
      setOptouts(o      || []);
      setByDpd(d        || []);
      setSummary(s      || {});
      setPrevSummary(ps || {});
    } catch (e) {
      setError("Failed to load opt-out data"); console.error(e);
    } finally {
      setLoading(false);
    }
  }, [params, prevParams]);

  const loadHeatmap = useCallback(async () => {
    setHmLoading(true);
    try {
      const h = await getOptoutsHeatmap({
        ...params,
        dpdBucket: dpdBucket === "All" ? undefined : dpdBucket,
      });
      setHeatmap(h || []);
    } catch (e) {
      console.error(e);
    } finally {
      setHmLoading(false);
    }
  }, [params, dpdBucket]);

  useEffect(() => { loadAll();     }, [loadAll]);
  useEffect(() => { loadHeatmap(); }, [loadHeatmap]);

  // ── KPIs ───────────────────────────────────────────────────────
  // All opt-out metrics are bad-when-up: more opt-outs = worse.
  // → buildDelta defaults goodWhenUp to false in this file.
  const kpis = useMemo(() => {
    const total     = safeNum(summary.total_optouts);
    const contacted = safeNum(summary.total_contacted);
    const rate      = contacted > 0 ? (total / contacted) * 100 : null;

    const prevTotal     = safeNum(prevSummary.total_optouts);
    const prevContacted = safeNum(prevSummary.total_contacted);
    const prevRate      = prevContacted > 0 ? (prevTotal / prevContacted) * 100 : null;

    const days     = daysInRange(params.startDate, params.endDate);
    const prevDays = daysInRange(prevParams.startDate, prevParams.endDate);
    const avgDay     = total     / days;
    const prevAvgDay = prevTotal / prevDays;

    // Peak DPD bucket — bucket with most opt-outs
    const sortedDpd = [...(byDpd || [])].sort(
      (a, b) => safeNum(b.optouts_total) - safeNum(a.optouts_total)
    );
    const peakDpd = sortedDpd.length > 0 && safeNum(sortedDpd[0].optouts_total) > 0
      ? sortedDpd[0]
      : null;

    return {
      total,
      rate:   rate == null ? "—" : `${fmt(rate, 2)}%`,
      avgDay: fmt(avgDay, 1),
      // Total = count (default fmt). Rate = percentage. Daily avg = 1 decimal.
      totalDelta:  buildDelta(total,  prevTotal),
      rateDelta:   buildDelta(rate,   prevRate,   { fmtVal: v => `${fmt(v, 2)}%` }),
      avgDayDelta: buildDelta(avgDay, prevAvgDay, { fmtVal: v => fmt(v, 1) }),
      peakDpd,
    };
  }, [summary, prevSummary, params, prevParams, byDpd]);

  // ── Chart data ─────────────────────────────────────────────────
  const optoutChartData = useMemo(() =>
    (optouts || []).map(r => ({
      date:  fmtDate(r.summary_date),
      count: safeNum(r.optouts_total),
      rate:  safeNum(r.optout_rate),
    }))
  , [optouts]);

  const cumulativeData = useMemo(() => {
    let running = 0;
    return optoutChartData.map(r => {
      running += r.count;
      return { date: r.date, cumulative: running };
    });
  }, [optoutChartData]);

  const dpdBarData = useMemo(() =>
    DPD_BUCKETS.map(b => {
      const row = (byDpd || []).find(r => r.dpd_bucket === b);
      return { dpd: b, count: safeNum(row?.optouts_total) };
    })
  , [byDpd]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Slicers ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <PillGroup
          label="Granularity"
          options={GRAN_OPTIONS}
          value={gran}
          onChange={setGran}
        />
        <PillGroup
          label="Date range"
          options={RANGE_OPTIONS}
          value={rangePreset}
          onChange={setRangePreset}
          groups={[4, 2, 4]}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignSelf: "flex-end" }}>
          <Typography sx={{
            fontSize: 11, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            Campaign
          </Typography>
          <Select
            value={campaign}
            onChange={e => setCampaign(e.target.value)}
            size="small" variant="outlined"
            sx={{
              fontSize: 12, borderRadius: "20px", minWidth: 120, height: 28,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "10px", fontSize: 12, color: "text.secondary" },
              ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
            }}
          >
            {campaigns.map(c => (
              <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading opt-out data…
        </Box>
      )}

      {!loading && (
        <>
          {/* ── Summary KPIs ── */}
          <SectionTitle>{ch} opt-outs — summary</SectionTitle>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            <KpiCard
              label={`Total ${ch.toLowerCase()} opt-outs`}
              value={kpis.total.toLocaleString()}
              color={PINK}
              delta={kpis.totalDelta}
            />
            <KpiCard label="Opt-out rate"  value={kpis.rate}   color={PINK}  delta={kpis.rateDelta}   />
            <KpiCard label="Daily average" value={kpis.avgDay} color={AMBER} delta={kpis.avgDayDelta} />
            <KpiCard
              label="Peak DPD bucket"
              value={kpis.peakDpd ? kpis.peakDpd.dpd_bucket : "—"}
              color={kpis.peakDpd ? (DPD_COLORS[kpis.peakDpd.dpd_bucket] || BLUE) : GRAY}
              delta={kpis.peakDpd ? {
                arrow: false,
                up: false,
                good: true, // green styling — neutral info
                text: `${safeNum(kpis.peakDpd.optouts_total).toLocaleString()} opt-outs`,
              } : null}
            />
          </Box>

          {/* ── Trend over time (full width) ── */}
          <ChartCard title={`${ch} opt-outs over time`} height={240}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={optoutChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis yAxisId="left"  tick={axisStyle} />
                <YAxis yAxisId="right" orientation="right"
                  tick={{ ...axisStyle, fill: AMBER }}
                  tickFormatter={v => `${v}%`} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Bar  yAxisId="left"  dataKey="count" name="Opt-outs"   fill={PINK}  radius={[3,3,0,0]} />
                <Line yAxisId="right" type="monotone" dataKey="rate"
                  name="Opt-out %" stroke={AMBER} dot={{ r: 3 }} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── Cumulative + DPD bar ── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mt: 1.5, mb: 1.5 }}>
            <ChartCard title="Cumulative opt-outs" height={200}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={cumulativeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line type="monotone" dataKey="cumulative" name="Cumulative"
                    stroke={PINK} strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Opt-outs by DPD bucket" height={200}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dpdBarData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="dpd" tick={axisStyle} />
                  <YAxis tick={axisStyle} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" name="Opt-outs" radius={[3,3,0,0]}>
                    {DPD_BUCKETS.map(b => (
                      <Cell key={b} fill={DPD_COLORS[b] || GRAY} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>

          {/* ── DPD breakdown table ── */}
          <SectionTitle>Breakdown by DPD bucket</SectionTitle>
          <OptoutByDpdTable data={byDpd} />

          {/* ── Day × Hour heatmap ── */}
          <SectionTitle>Opt-out heatmap — day × hour</SectionTitle>
          <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px", mb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 11, color: "text.secondary",
                textTransform: "uppercase", letterSpacing: "0.05em", mr: 0.5 }}>
                Filter by DPD
              </Typography>
              {["All", ...DPD_BUCKETS].map(b => {
                const active = dpdBucket === b;
                const color  = DPD_COLORS[b] || PINK;
                return (
                  <Box key={b} onClick={() => setDpdBucket(b)} sx={{
                    px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                    cursor: "pointer", userSelect: "none", border: "0.5px solid",
                    borderColor: active ? color : "divider",
                    background:  active ? color : "transparent",
                    color:       active ? "#fff" : "text.secondary",
                    fontWeight:  active ? 500 : 400,
                    transition: "all .15s",
                    "&:hover": { borderColor: color },
                  }}>{b}</Box>
                );
              })}
              {hmLoading && <CircularProgress size={14} sx={{ ml: 0.5, color: PINK }} />}
            </Box>
            <Heatmap data={heatmap} />
          </Box>
        </>
      )}
    </Box>
  );
}